import { Link, useParams } from 'react-router-dom';

import { PageHeader } from '../../../components/PageHeader';
import { invariant } from '../../../utils/invariant';
import { DataField } from '../../../components/DataField';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { PageHeading } from 'components/PageHeading';
import { useAuditLog } from '../models';
import { SpinnerBlock } from '../../../components/Spinner';
import { CustomError, ErrorCode } from 'src/error';
import { ErrorState } from 'components/error-state/ErrorState';

export const AuditLogPage = () => {
  const { auditLogId } = useParams<{ auditLogId: string }>();
  invariant(auditLogId);

  const { data: auditLog, error, isLoading, mutate } = useAuditLog(auditLogId);

  if (isLoading) {
    return <SpinnerBlock message="Audit log laden..." />;
  }

  if (error || !auditLog) {
    const isNotFound = (error instanceof CustomError && error.code === ErrorCode.NOT_FOUND) || (!error && !auditLog);

    return (
      <ErrorState
        error={error}
        title={isNotFound ? 'Audit log niet gevonden' : 'Audit log kon niet worden geladen'}
        description={isNotFound ? 'De gevraagde audit log werd niet gevonden.' : undefined}
        onRetry={!isNotFound && error ? () => mutate() : undefined}
        variant="section"
      />
    );
  }

  return (
    <>
      <PageHeader title="Audit Log" />

      <PageHeading
        noMarginBottom
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Audit Logs',
              to: '/internal/audit-logs',
            }}
            currentItem="Audit Log"
          />
        }
      />

      <div className="p-7">
        <div className="card flex flex-col gap-3.5 p-5">
          <DataField title="Actor">{auditLog.actorName}</DataField>
          <DataField title="Actie">{auditLog.actionType}</DataField>
          <DataField title="Beschrijving">{auditLog.description}</DataField>
          {auditLog.order != null && (
            <DataField title="Order">
              <Link to={`/internal/orders/${auditLog.order.id}/general`} className="link-text">
                {auditLog.order.orderNumber ?? 'DRAFT'}
              </Link>
            </DataField>
          )}
          {auditLog.customer != null && (
            <DataField title="Klant">
              <Link to={`/internal/customers/${auditLog.customer.id}/general`} className="link-text">
                {auditLog.customer.name}
              </Link>
            </DataField>
          )}
          {auditLog.location != null && (
            <DataField title="Locatie">
              <Link to={`/internal/locations/${auditLog.location.id}/general`} className="link-text">
                {auditLog.location.name}
              </Link>
            </DataField>
          )}
          {auditLog.supplier != null && (
            <DataField title="Vervoerder">
              <Link to={`/internal/suppliers/${auditLog.supplier.id}/general`} className="link-text">
                {auditLog.supplier.name}
              </Link>
            </DataField>
          )}
          {auditLog.details && (
            <DataField title="Details">
              <div className="whitespace-pre-line bg-neutral-subtle rounded p-2">{auditLog.details}</div>
            </DataField>
          )}
        </div>
      </div>
    </>
  );
};
